/**
 * Parses a Prime Video URL and extracts the Sonata pageType and pageId.
 *
 * Supports URLs from primevideo.com, amazon.com/gp/video, watch.amazon.com,
 * and app.primevideo.com. The parsing logic mirrors DVWebNode's swiftPageParams.ts
 * (https://code.amazon.com/packages/DVWebNode/blobs/mainline/--/packages/lib-legacy-util/src/util/swiftPageParams.ts).
 * (https://w.amazon.com/bin/view/ATV/Website/Documentation/LandingPageURLs/)
 */

export interface ParsedPageParams {
  pageType: string;
  pageId: string;
}

/** Static segment → Swift page params (from DVWebNode pageKeywords.ts) */
const STATIC_SEGMENTS: Record<string, ParsedPageParams> = {
  tv: { pageType: 'tv', pageId: 'home' },
  movie: { pageType: 'movie', pageId: 'home' },
  kids: { pageType: 'merch', pageId: 'kids' },
  livetv: { pageType: 'home', pageId: 'live' },
  sports: { pageType: 'home', pageId: 'Sports' },
  store: { pageType: 'home', pageId: 'store' },
  addons: { pageType: 'home', pageId: 'addons' },
  news: { pageType: 'home', pageId: 'news' },
  streamfree: { pageType: 'merch', pageId: 'streamfree' },
};

/** Segment + sub-segment → Swift page params (store/X, mystuff/X) */
const SUB_SEGMENT_MAP: Record<string, Record<string, ParsedPageParams>> = {
  store: {
    'rent-or-buy': { pageType: 'home', pageId: 'store' },
    channels: { pageType: 'home', pageId: 'store' },
    deals: { pageType: 'merch', pageId: 'deals' },
  },
  mystuff: {
    watchlist: { pageType: 'home', pageId: 'Watchlist' },
    library: { pageType: 'home', pageId: 'VideoLibrary' },
  },
};

/** Segments where the sub-segment is the pageId directly */
const SEGMENT_TO_PAGE_TYPE: Record<string, string> = {
  tournament: 'tournament',
  channel: 'channel',
  collection: 'merch',
  partner: 'partner',
};

const PV_DOMAINS = [
  'primevideo.com',
  'amazon.com',
  'amazon.co.uk',
  'amazon.co.jp',
  'amazon.de',
  'watch.amazon.com',
  'app.primevideo.com',
];

function isPrimeVideoUrl (hostname: string): boolean {
  return PV_DOMAINS.some(domain => hostname === domain || hostname === `www.${domain}`);
}

/**
 * Strips common PV path prefixes to get the meaningful path segments.
 * e.g. "/gp/video/genre/action" → ["genre", "action"]
 *      "/genre/action" → ["genre", "action"]
 *      "/storefront/genre/av_genre_action" → ["storefront", "genre", "av_genre_action"]
 *      "/region/eu/genre/action" → ["genre", "action"]
 */
function extractPathSegments (pathname: string): string[] {
  let path = pathname;

  // Strip trailing slash
  if (path.endsWith('/')) path = path.slice(0, -1);

  // Strip /gp/video prefix (retail domains)
  path = path.replace(/^\/gp\/video/, '');

  // Strip /region/{region} prefix (primevideo.com regional URLs)
  path = path.replace(/^\/region\/[a-z]+/i, '');

  // Strip /hp/video prefix (used in some internal contexts)
  path = path.replace(/^\/hp\/video/, '');

  const segments = path.split('/').filter(Boolean);

  // Remove ref= tags from last segment
  if (segments.length > 0 && segments[segments.length - 1].startsWith('ref=')) {
    segments.pop();
  }

  return segments;
}

/**
 * Parses a Prime Video URL into Sonata pageType and pageId.
 * Returns null if the URL is not a recognized PV URL or cannot be parsed.
 *
 * Examples:
 *   "https://www.primevideo.com/genre/young-adult-audience"
 *     → { pageType: 'genre', pageId: 'av_genre_young_adult_audience' }
 *
 *   "https://www.primevideo.com/storefront/merch/IncludedwithPrime"
 *     → { pageType: 'merch', pageId: 'IncludedwithPrime' }
 *
 *   "https://www.amazon.com/gp/video/storefront?contentType=genre&contentId=av_genre_drama"
 *     → { pageType: 'genre', pageId: 'av_genre_drama' }
 */
export function parsePrimeVideoUrl (url: string): ParsedPageParams | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (!isPrimeVideoUrl(parsed.hostname)) {
    return null;
  }

  // 1. Check query parameters first (contentType/contentId, benefitId, merchId)
  const contentType = parsed.searchParams.get('contentType');
  const contentId = parsed.searchParams.get('contentId');
  if (contentType && contentId) {
    return { pageType: contentType, pageId: contentId };
  }

  const benefitId = parsed.searchParams.get('benefitId');
  if (benefitId) {
    return { pageType: 'subscription', pageId: benefitId };
  }

  const merchId = parsed.searchParams.get('merchId');
  if (merchId) {
    return { pageType: 'merch', pageId: merchId };
  }

  // 2. Parse path-based URLs
  const segments = extractPathSegments(parsed.pathname);
  if (segments.length === 0) {
    return null;
  }

  // /storefront/{pageType}/{pageId} — generic passthrough
  if (segments[0] === 'storefront' && segments.length >= 3) {
    return { pageType: segments[1], pageId: segments[2] };
  }

  // /landing?pageType=X&pageId=Y (watch.amazon.com deep links)
  if (segments[0] === 'landing') {
    const pageType = parsed.searchParams.get('pageType');
    const pageId = parsed.searchParams.get('pageId');
    if (pageType && pageId) {
      return { pageType, pageId };
    }
    return null;
  }

  // Two-segment paths: segment/sub-segment
  if (segments.length >= 2) {
    const [segment, subSegment] = segments;

    // Genre: special transform (from DVWebNode swiftPageParams.ts)
    if (segment === 'genre') {
      return {
        pageType: 'genre',
        pageId: `av_genre_${subSegment.replace(/-/g, '_')}`,
      };
    }

    // Sub-segment maps (store/X, mystuff/X)
    const subSegmentResult = SUB_SEGMENT_MAP[segment]?.[subSegment];
    if (subSegmentResult) {
      return subSegmentResult;
    }

    // Dynamic segment → pageType mapping (tournament, channel, collection, partner)
    const pageType = SEGMENT_TO_PAGE_TYPE[segment];
    if (pageType) {
      return { pageType, pageId: subSegment };
    }
  }

  // Single-segment static pages (/tv, /movie, /kids, etc.)
  if (segments.length === 1) {
    const staticResult = STATIC_SEGMENTS[segments[0]];
    if (staticResult) {
      return staticResult;
    }
  }

  return null;
}
